import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Product, Category, CartItem, Order, Language } from '../types';
import { MIN_QUANTITY } from '../constants';
import { productsApi, categoriesApi, ordersApi } from '../services/apiService';
import { getDefaultLanguage, saveLanguagePreference } from '../config/app.config';

interface StoreContextType {
  products: Product[];
  categories: Category[];
  cart: CartItem[];
  orders: Order[];
  myOrders: Order[];
  language: Language;
  searchQuery: string;
  searchResults: Product[];
  loading: boolean;
  setSearchQuery: (query: string) => void;
  setLanguage: (lang: Language) => void;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  placeOrder: (customer: Order['customer']) => Promise<{ success: boolean; order?: Order; error?: string }>;
  addProduct: (product: Product) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (product: Product) => void;
  updateCategory: (category: Category) => void;
  addCategory: (category: Category) => void;
  deleteCategory: (id: string) => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => void;
  updateOrder: (order: Order) => void;
  deleteOrder: (id: string) => void;
  refreshOrders: () => Promise<void>;
  getMyOrder: (orderId: string) => Order | undefined;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'dido-cart';
const MY_ORDERS_STORAGE_KEY = 'dido-my-orders';

const loadCartFromStorage = (): CartItem[] => {
  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (storedCart) {
      const parsed = JSON.parse(storedCart);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to load cart from localStorage:', error);
  }
  return [];
};

const saveCartToStorage = (cart: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.warn('Failed to save cart to localStorage:', error);
  }
};

const loadMyOrdersFromStorage = (): Order[] => {
  try {
    const storedOrders = localStorage.getItem(MY_ORDERS_STORAGE_KEY);
    if (storedOrders) {
      const parsed = JSON.parse(storedOrders);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to load orders from localStorage:', error);
  }
  return [];
};

const saveMyOrdersToStorage = (orders: Order[]): void => {
  try {
    localStorage.setItem(MY_ORDERS_STORAGE_KEY, JSON.stringify(orders));
  } catch (error) {
    console.warn('Failed to save orders to localStorage:', error);
  }
};

// Retry helper function with exponential backoff
const retryWithExponentialBackoff = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on client errors (4xx), only retry on network errors or 5xx
      if (error?.response?.status && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Calculate exponential backoff delay: 1s, 2s, 4s
      const delay = initialDelay * Math.pow(2, attempt);
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`, error.message || error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Transform backend product to frontend format
const transformProduct = (backendProduct: any): Product => {
  return {
    id: backendProduct.id,
    name: backendProduct.name,
    slug: backendProduct.slug,
    price: typeof backendProduct.price === 'string' ? parseFloat(backendProduct.price) : backendProduct.price,
    categoryId: backendProduct.category_id || backendProduct.categoryId,
    description: backendProduct.description,
    image: backendProduct.image,
    stock: backendProduct.stock,
    discount: backendProduct.discount || 0,
    featured: backendProduct.featured || false,
    // admin_note is intentionally included here so admin views can display it;
    // public pages must never render this field
    adminNote: backendProduct.admin_note ?? backendProduct.adminNote ?? null,
    minQuantity: backendProduct.min_quantity || backendProduct.minQuantity || 6,
    createdAt: backendProduct.created_at || backendProduct.createdAt,
    updatedAt: backendProduct.updated_at || backendProduct.updatedAt
  };
};

// Transform frontend product to backend format
const transformProductToBackend = (product: Product): any => {
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    price: product.price,
    category_id: product.categoryId,
    description: product.description,
    image: product.image,
    stock: product.stock || 0,
    discount: product.discount || 0,
    featured: product.featured || false,
    admin_note: product.adminNote || null,
    min_quantity: product.minQuantity || 6
  };
};

// Transform backend category to frontend format
const transformCategory = (backendCategory: any): Category => {
  return {
    id: backendCategory.id,
    name: {
      en: backendCategory.name_en || backendCategory.name?.en || '',
      pl: backendCategory.name_pl || backendCategory.name?.pl || ''
    },
    slug: backendCategory.slug
  };
};

// Transform frontend category to backend format
const transformCategoryToBackend = (category: Category): any => {
  return {
    id: category.id,
    name_en: category.name.en,
    name_pl: category.name.pl,
    slug: category.slug
  };
};

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cart, setCart] = useState<CartItem[]>(loadCartFromStorage());
  const [orders, setOrders] = useState<Order[]>([]);
  const [myOrders, setMyOrders] = useState<Order[]>(loadMyOrdersFromStorage());
  const [language, setLanguage] = useState<Language>(getDefaultLanguage());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<Product[]>([]);

  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  useEffect(() => {
    saveMyOrdersToStorage(myOrders);
  }, [myOrders]);

  // Save language preference when it changes
  useEffect(() => {
    saveLanguagePreference(language);
  }, [language]);

  // Search products from backend when query changes
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim()) {
        try {
          const response = await productsApi.getAll({ search: searchQuery });
          if (response.success && response.data) {
            const transformedProducts = (response.data as any[]).map(transformProduct);
            setSearchResults(transformedProducts);
          }
        } catch (error) {
          console.error('Error searching products:', error);
          // Fallback to client-side search
          const filtered = products.filter(p => 
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            p.description.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setSearchResults(filtered);
        }
      } else {
        setSearchResults([]);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchProducts();
    }, 300); // Debounce search

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, products]);

  // Fetch initial data from backend with retry logic
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch products and categories in parallel with retry logic
        const [productsResponse, categoriesResponse] = await Promise.all([
          retryWithExponentialBackoff(() => productsApi.getAll(), 3, 1000),
          retryWithExponentialBackoff(() => categoriesApi.getAll(), 3, 1000)
        ]);

        if (productsResponse.success && productsResponse.data) {
          const transformedProducts = (productsResponse.data as any[]).map(transformProduct);
          setProducts(transformedProducts);
        } else {
          console.error('Failed to fetch products from API');
          setProducts([]);
        }

        if (categoriesResponse.success && categoriesResponse.data) {
          const transformedCategories = (categoriesResponse.data as any[]).map(transformCategory);
          setCategories(transformedCategories);
        } else {
          console.error('Failed to fetch categories from API');
          setCategories([]);
        }

        // Orders are fetched on-demand in admin dashboard only
        // to avoid unnecessary API calls on public pages
      } catch (error) {
        console.error('Error fetching initial data after all retries:', error);
        // Set empty arrays on error - backend should be available
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const addToCart = (product: Product, quantity?: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      const qty = quantity ?? (product.minQuantity || MIN_QUANTITY);
      
      if (existing) {
        return prev.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + qty } : item
        );
      }
      return [...prev, { ...product, quantity: qty }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item => (item.id === productId ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => setCart([]);

  const placeOrder = async (customer: Order['customer']): Promise<{ success: boolean; order?: Order; error?: string }> => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const vat = subtotal * 0.23;
    const total = subtotal + vat;

    const newOrder: Order = {
      id: Math.random().toString(36).substr(2, 9),
      items: [...cart],
      total,
      subtotal,
      vat,
      customer,
      status: 'pending',
      date: new Date().toISOString(),
    };

    try {
      const response = await retryWithExponentialBackoff(
        () => ordersApi.create(newOrder),
        3,
        1000
      );

      if (response.success && response.data) {
        const savedOrder = response.data as Order;
        setOrders(prev => [savedOrder, ...prev]);
        setMyOrders(prev => [savedOrder, ...prev]);
        clearCart();
        console.log('Order placed successfully');
        return { success: true, order: savedOrder };
      }

      const message = response.message || 'Order could not be created';
      console.warn('Order API returned unsuccessful response:', message);
      return { success: false, order: newOrder, error: message };
    } catch (error: any) {
      const message = error?.message || 'Network error while creating order';
      console.error('Error creating order after all retry attempts:', error);
      return { success: false, order: newOrder, error: message };
    }
  };

  const getMyOrder = (orderId: string): Order | undefined => {
    return myOrders.find(o => o.id === orderId);
  };

  const addProduct = async (product: Product) => {
    try {
      const backendProduct = transformProductToBackend(product);
      const response = await productsApi.create(backendProduct);
      if (response.success && response.data) {
        const transformedProduct = transformProduct(response.data);
        setProducts(prev => [...prev, transformedProduct]);
      } else {
        // Fallback to local state
        setProducts(prev => [...prev, product]);
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setProducts(prev => [...prev, product]);
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      await productsApi.delete(id);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      // Still remove from local state
      setProducts(prev => prev.filter(p => p.id !== id));
    }
  };
  
  const updateProduct = async (product: Product) => {
    try {
      const backendProduct = transformProductToBackend(product);
      const response = await productsApi.update(product.id, backendProduct);
      if (response.success) {
        setProducts(prev => prev.map(p => p.id === product.id ? product : p));
      }
    } catch (error) {
      console.error('Error updating product:', error);
      // Still update local state
      setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    }
  };

  const updateCategory = async (category: Category) => {
    try {
      const backendCategory = transformCategoryToBackend(category);
      const response = await categoriesApi.update(category.id, backendCategory);
      if (response.success) {
        setCategories(prev => prev.map(c => c.id === category.id ? category : c));
      }
    } catch (error) {
      console.error('Error updating category:', error);
      setCategories(prev => prev.map(c => c.id === category.id ? category : c));
    }
  };

  const addCategory = async (category: Category) => {
    try {
      const backendCategory = transformCategoryToBackend(category);
      const response = await categoriesApi.create(backendCategory);
      if (response.success && response.data) {
        const transformedCategory = transformCategory(response.data);
        setCategories(prev => [...prev, transformedCategory]);
      } else {
        setCategories(prev => [...prev, category]);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      setCategories(prev => [...prev, category]);
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await categoriesApi.delete(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      setCategories(prev => prev.filter(c => c.id !== id));
    }
  };

  const updateOrderStatus = async (orderId: string, status: Order['status']) => {
    try {
      const response = await ordersApi.updateStatus(orderId, status);
      if (response.success) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    }
  };

  const updateOrder = async (order: Order) => {
    try {
      const response = await ordersApi.update(order.id, order);
      if (response.success) {
        setOrders(prev => prev.map(o => o.id === order.id ? order : o));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setOrders(prev => prev.map(o => o.id === order.id ? order : o));
    }
  };

  const deleteOrder = async (id: string) => {
    try {
      await ordersApi.delete(id);
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch (error) {
      console.error('Error deleting order:', error);
      setOrders(prev => prev.filter(o => o.id !== id));
    }
  };

  // Security: Refresh orders from backend - requires authentication
  // This function should be called after admin login to fetch orders
  // Wrapped in useCallback to prevent infinite loops in useEffect
  const refreshOrders = useCallback(async () => {
    try {
      const ordersResponse = await ordersApi.getAll();
      if (ordersResponse.success && ordersResponse.data) {
        setOrders(ordersResponse.data as Order[]);
      }
    } catch (error) {
      console.error('Error refreshing orders:', error);
    }
  }, []); // No dependencies - function is stable

  return (
    <StoreContext.Provider
      value={{
        products,
        categories,
        cart,
        orders,
        myOrders,
        language,
        searchQuery,
        searchResults,
        loading,
        setSearchQuery,
        setLanguage,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        placeOrder,
        addProduct,
        deleteProduct,
        updateProduct,
        updateCategory,
        addCategory,
        deleteCategory,
        updateOrderStatus,
        updateOrder,
        deleteOrder,
        refreshOrders,
        getMyOrder
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used within StoreProvider');
  return context;
};